import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Observable, switchMap, takeUntil } from 'rxjs';

import type { Entry } from '../../../features/admin/modules/modules.model';
import { LinkButtonComponent } from '../link-button.component';
import { ModalService, runnerDialogRef } from '../../services/modal.service';
import { UtilityService } from '../../services/utility.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { resolveCreatedEntry, type CreateRunnerCloseResult } from '../../utils/create-runner-result.util';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyFieldAutocomplete } from './autocomplete.component';
import { AddressbookSearchService } from './services/addressbook-search.service';
import {
  AddressbookOption,
  AddressbookOptionSourceEnum,
  isUnsavedAddressbookEntry,
} from './services/addressbook-search.types';
import { commitUserValue } from './user-edit.util';

/**
 * Addressbook field: one autocomplete over two sources.
 *
 * Saved Adressenboek entries come first (book glyph), external Maps suggestions after them (pin
 * glyph). Whichever the user picks, the stored value is the SAME addressbook `Entry` shape — a
 * Maps hit is converted, not stored raw — so swapping an existing `autocomplete` field over to
 * this type needs no data migration. Typing alone never produces a value: without a pick the
 * field stays empty.
 *
 * ## Why a separate type instead of a prop on `autocomplete`
 *
 * `addressStyle`, `kvkStyle` and `rdwStyle` are style variants of one behaviour inside one
 * component. This is a different shape of field: two independent async sources, a merged list, a
 * different paging contract, and a write flow back into the source module. Branching on a prop
 * inside `FormlyFieldAutocomplete` would put all of that in the path EVERY module-entry
 * autocomplete runs through. One codebase serves every tenant, so the expensive mistake is not
 * customer-specific code but a customer assumption in a SHARED path. Subclassing keeps the
 * shared path untouched and this field inert for any module that does not pick it.
 *
 * ## What it overrides
 *
 * The four option-source seams (`freshOptionsSource`, `moreOptionsSource`, `pageableCount`,
 * `mergeMoreOptions`) plus three behaviours that a second, unsaved-value source changes:
 * `validateOptionExists`, `onOptionSelected` and `ngOnInit`. The search state machine — pagination
 * cursor, loading flags, out-of-order guard, error recovery, clearing — is inherited whole, so a
 * later fix there lands here too. Everything else (option hydration for a saved value, the
 * dynamic-options plumbing, the scroll handler, cascading auto-fill, the dirty-marking contract) is
 * inherited untouched.
 *
 * ## Deliberately NOT here
 *
 * `[class.hidden]="isFieldHidden()"`, which the generic autocomplete carries on this same wrapper.
 * `isFieldHidden` is driven solely by `hideIfOptionsEmpty`, and the form builder keeps that
 * setting closed for this type (its `hide` expression only opens for `tags` and `autocomplete`).
 * That is the right call: this field's list is empty until the user types, so `hideIfOptionsEmpty`
 * would hide it permanently. Binding it here would be dead code guarding a setting no admin can
 * reach. Role-based hiding is a different mechanism and is unaffected.
 */
@Component({
  selector: 'formly-field-addressbook-autocomplete',
  template: `
    <div class="w-full">
      <mat-form-field
        class="w-full"
        [floatLabel]="isDisabled() ? 'always' : 'auto'"
        [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
        @if (isLoading()) {
          <mat-spinner class="absolute right-8" diameter="24"></mat-spinner>
        }
        @if (props.label) {
          <mat-label>
            <app-field-label-row
              [label]="props.label"
              [required]="props.required"
              [info]="props.info"></app-field-label-row>
          </mat-label>
        }
        <input
          #searchInput
          type="text"
          [required]="props.required"
          [placeholder]="isDisabled() ? '' : props.placeholder || 'Zoek op naam of adres'"
          [formlyDataCy]="field"
          matInput
          [formControl]="searchField"
          [matAutocomplete]="auto"
          (focus)="onInputFocus()"
          class="form-control" />
        <!-- Same suffix cell as the generic autocomplete: dd-addon-suffix stacks the chevron and
             the clear button so both land on Figma's 12px trailing inset. -->
        <div matSuffix class="dd-clear-suffix dd-addon-suffix">
          @if (filteredOptions().length && isDisabled() === false) {
            <mat-icon class="dd-addon-suffix__passive dd-trailing-glyph" svgIcon="feather:chevron-down"></mat-icon>
          }
          <app-clear-field-button
            [field]="field"
            [hasValue]="hasClearableValue()"
            [disabled]="isDisabled()"
            [ariaLabel]="clearAriaLabel()"
            [focusTarget]="searchInput"
            (clearSelection)="clearValue()" />
        </div>

        <!--
          One flat list, no mat-optgroup. Both sources arrive in a single emission, so a heading
          would not flicker WITHIN a query — but the external half is empty for plenty of queries
          and the addressbook half is empty on a fresh tenant, so headings would still appear and
          disappear between queries, above a single row. A dropdown row is also the wrong place to
          spend a whole line of vertical space on chrome. The per-row glyph plus the "Opgeslagen"
          tag carry the same fact and never move; the sr-only text carries it for anyone who cannot
          see either.
        -->
        <mat-autocomplete
          #auto="matAutocomplete"
          (opened)="onAutocompleteOpened()"
          (optionSelected)="onOptionSelected($event)"
          [panelWidth]="'auto'">
          @for (option of filteredOptions(); track option.value?.id || option.label) {
            <mat-option
              [value]="option.label"
              [disabled]="option.disabled === true"
              [attr.data-cy]="option.source === mapsSource ? 'addressbook-option-maps' : 'addressbook-option-book'">
              <div class="flex items-center gap-2">
                <!-- No colour class here. text-hint is #A9A9AD in BOTH schemes (theming.js) and
                     measures 2.34:1 on the light option panel — under the 3:1 that applies to
                     non-text carrying meaning, and this glyph is the only thing separating the two
                     sources. Inheriting the option's own colour keeps it readable in light as well
                     as dark. -->
                <mat-icon
                  class="icon-size-5 flex-shrink-0"
                  [svgIcon]="
                    option.source === mapsSource ? 'heroicons_outline:map-pin' : 'heroicons_outline:book-open'
                  ">
                </mat-icon>
                <!-- The glyph is aria-hidden, so without this a screen reader hears two identical
                     address lines and cannot tell which row is already saved. -->
                <!-- "Gevonden" would not contrast: both halves are results of the same search.
                     Saved versus not-saved is the distinction that matters, because it is exactly
                     what the add-link below the field keys on. -->
                <span class="sr-only">{{ option.source === mapsSource ? 'Nieuw adres' : 'Opgeslagen adres' }}</span>
                <!-- One line per row. The generic autocomplete gets its second line from
                     additionalInfoTemplate, a builder setting scoped to the plain autocomplete
                     type, so rendering it here would be a branch no admin can ever fill.
                     Note what that costs: an external row composes to a full address, but a saved
                     row shows whatever moduleLinkedField points at — usually just a name. Two
                     similarly named depots are then hard to tell apart. Showing the address as a
                     second line is a product decision, not a gap to paper over here.
                     NOTE: no backticks in this comment — the template is a template literal and a
                     backtick here ends the string mid-component. -->
                <span class="min-w-0 flex-1 leading-4">{{ option.label }}</span>
                <!-- The glyph alone only works for someone who has already decoded book-vs-pin.
                     Colour comes from .dd-option-source-tag (styles/components/_forms.scss), NOT
                     from the text-accent utility the rest of this family uses: that one reads
                     --fuse-accent, which has no dark override and measures 2.24:1 on the dark
                     option panel. Same Figma role, same slot, correct in both schemes. -->
                @if (option.source !== mapsSource) {
                  <span class="dd-option-source-tag ml-auto shrink-0">Opgeslagen</span>
                }
              </div>
            </mat-option>
          }
          <!--
            Gated on an actual search term. Focusing the field runs an empty-term query
            (onInputFocus -> searchWithoutFilter) and the external lookup deliberately skips a
            blank query, so on a tenant with an empty addressbook this row used to greet the user
            with "Geen adres gevonden" before they had typed anything.
          -->
          @if (searched() && currentSearchTerm() !== '' && filteredOptions().length === 0 && !loading()) {
            <mat-option [disabled]="true">Geen adres gevonden</mat-option>
          }
          @if (loading()) {
            <mat-option disabled>
              <div class="flex justify-center">
                <mat-spinner diameter="24"></mat-spinner>
              </div>
            </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>

      <!--
        Only after picking an external suggestion: an addressbook row is already saved, so there is
        nothing to add. Its own row under the field, not next to the input — the generic
        autocomplete's "+" button sits there, and two add affordances side by side is exactly the
        confusion to avoid. That button is suppressed for this type anyway (see ngOnInit).
      -->
      @if (canAddToAddressbook() && !isDisabled()) {
        <div class="mb-4">
          <button
            type="button"
            dd-link-button
            size="sm"
            icon="heroicons_outline:plus"
            iconClass="icon-size-4"
            (click)="addToAddressbook()"
            data-cy="addressbook-add">
            Adres toevoegen aan {{ targetModuleName() }}
          </button>
        </div>
      }

      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
    </div>
    @if (field.formControl.errors) {
      <app-formly-error [field]="field"></app-formly-error>
    }
  `,
  // NOT inherited: Angular does not hand a parent component's `styles` down to a subclass. In the
  // running app these rules happen to be present anyway, because ::ng-deep leaks them globally the
  // moment any plain autocomplete has rendered — but in Storybook, where this field renders on its
  // own, the panel collapses narrower than the input. Copied rather than shared for that reason.
  styles: `
    ::ng-deep .mdc-list-item__primary-text {
      width: 100%;
      display: inline-block;
    }

    ::ng-deep .cdk-overlay-pane {
      min-width: 320px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatFormField,
    MatLabel,
    MatHint,
    MatSuffix,
    MatInput,
    MatIcon,
    MatProgressSpinner,
    FormlyDataCyDirective,
    FormlyErrorComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
    LinkButtonComponent,
  ],
})
export class FormlyFieldAddressbookAutocomplete extends FormlyFieldAutocomplete {
  readonly #addressbookSearch = inject(AddressbookSearchService);
  readonly #modalService = inject(ModalService);
  readonly #utilityService = inject(UtilityService);

  /**
   * True once the user has typed since the last pick.
   *
   * Needed because the base only clears the bound value when the box is emptied COMPLETELY: type
   * one more character after picking an external suggestion and the old value survives, which
   * would leave the add-link offering to save an address the field no longer shows.
   *
   * Every programmatic write to `searchField` is silent (`{ emitEvent: false }`), including the
   * one in the base `clearValue`, so a `valueChanges` means the user typed. Clearing needs no
   * handling of its own: it nulls the bound value, and the link is gated on that value as well.
   */
  readonly #userTypedSincePick = signal(false);

  /**
   * The link only makes sense for a value this field built from an external suggestion: no entry
   * id means it exists nowhere but in this form. A picked addressbook row always has one.
   */
  readonly canAddToAddressbook = computed(
    () => !this.#userTypedSincePick() && isUnsavedAddressbookEntry(this.currentValue())
  );

  /**
   * Which glyph and which label a row gets. A NEGATIVE test on purpose: only this component's own
   * service stamps a `source`, while three inherited paths write raw module options straight into
   * `filteredOptions` (hydrating a saved value, the dynamic-options callback, the merge inside
   * validation). Those rows are addressbook rows, so "anything not explicitly external is a book
   * row" labels them correctly — where a positive test on `'book'` would have put a map pin next
   * to every hydrated saved value. Bound in the template so the per-row comparison is a field
   * read, not a method call.
   */
  protected readonly mapsSource = AddressbookOptionSourceEnum.Maps;

  /**
   * The linked module's own name, for the add-link.
   *
   * Which module this field writes into is admin configuration (`optionsArray[0].moduleCode`).
   * A tenant that points it at "Locaties" or "Depots" would otherwise read an offer to add to an
   * "adresboek" that appears nowhere in their navigation — and even on the default config the
   * module is called "Adressenboek", not "adresboek". The fallback only shows before the linked
   * data has resolved.
   */
  readonly targetModuleName = computed(() => this.linkedData()[0]?.module?.name ?? 'het adresboek');

  isMapsRow(option: AddressbookOption): boolean {
    return option?.source === this.mapsSource;
  }

  override ngOnInit(): void {
    // Typing must never produce a value on its own: the stored answer is an addressbook entry, so
    // a half-typed string is not a lesser value, it is a different kind of thing. Set before
    // super, because the inherited search handler reads this flag on every keystroke — and
    // because the inherited template guards on it being defined at all.
    // Both writes guarded like `moduleValueType` below: `props` can be live store state.
    if (this.props.requireSelection !== true) {
      this.props.requireSelection = true;
    }
    // One add affordance, not two. The inherited "+" opens the same create flow the link under
    // the field opens, and the designer's call was the link.
    if (this.props.showCreateButton !== false) {
      this.props.showCreateButton = false;
    }
    // The whole field rests on the value being a full entry: the add-link, the de-duplication, the
    // unsaved-value exemption and the readonly display all read `value.entry.address`. The form
    // builder offers `id` / `entry-small` / `field` here like it does for any module source, and
    // every one of them breaks those four silently rather than loudly. Forced here rather than
    // gated in the builder so an existing misconfiguration is corrected too, not just a new field.
    // Guarded on inequality rather than written unconditionally: `props` can be the live object
    // out of the module store, and a component has no business rewriting store state on every
    // mount. This way the only write that ever happens is the one correcting a misconfiguration.
    const source = this.props.optionsArray?.[0];
    if (source?.optionsType === 'module' && source.moduleValueType !== 'entry') {
      source.moduleValueType = 'entry';
    }
    super.ngOnInit();

    this.searchField.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.#userTypedSincePick.set(true);
    });
  }

  /**
   * Both sources for a fresh query: saved addressbook entries first, external suggestions after.
   *
   * The external lookup is always part of the query — searching both sources IS this field type,
   * and a module that only wants its own addressbook already has the plain `autocomplete` type.
   * Deliberately not behind a prop: a prop `form-generate.util.mjs` does not emit never reaches
   * the field, which is the trap that file documents at :384-386 (#20811).
   */
  protected override freshOptionsSource(
    searchTerm: string,
    offset: number,
    limit: number
  ): Observable<AddressbookOption[]> {
    return this.#addressbookSearch.search(
      this.dynamicFilterValue(),
      this.linkedData(),
      searchTerm,
      this.field,
      offset,
      limit
    );
  }

  /**
   * Scroll-to-load asks the addressbook only.
   *
   * `/addresses/search` takes no offset, limit or cursor, so there is no second external page to
   * request; asking again returns the batch already on screen.
   */
  protected override moreOptionsSource(
    searchTerm: string,
    offset: number,
    limit: number
  ): Observable<AddressbookOption[]> {
    return this.#addressbookSearch.searchBook(
      this.dynamicFilterValue(),
      this.linkedData(),
      searchTerm,
      this.field,
      offset,
      limit
    );
  }

  /**
   * Count the addressbook half only: a short page THERE means that source is exhausted. Counting
   * the merged list would stop addressbook paging the moment the external half happened to fill
   * the page.
   */
  protected override pageableCount(options: AddressbookOption[]): number {
    return options.filter(option => !this.isMapsRow(option)).length;
  }

  /**
   * New addressbook rows go in BEFORE the first external row, so the two sources stay grouped as
   * the list grows; appending would interleave saved addresses among the external ones from page
   * two onwards. They are also de-duplicated against the external rows already on screen —
   * `search()` only de-duplicates what it fetched itself, so without this a saved address on page
   * two would end up next to its own external copy, add-link and all.
   */
  protected override mergeMoreOptions(
    current: AddressbookOption[],
    newOptions: AddressbookOption[]
  ): AddressbookOption[] {
    const book = current.filter(option => !this.isMapsRow(option));
    const maps = current.filter(option => this.isMapsRow(option));
    // `super` keeps the id de-dup between overlapping addressbook pages (NG0955 otherwise).
    return [
      ...(super.mergeMoreOptions(book, newOptions) as AddressbookOption[]),
      ...this.#addressbookSearch.withoutDuplicates(maps, newOptions),
    ];
  }

  /**
   * Both sources take the inherited path; only the typed-since-pick flag is ours.
   *
   * There is deliberately NO separate branch for an external pick. The base resolves the row by
   * label out of `filteredOptions` and commits `option.value` — and by the time a row is on screen
   * this field has already converted it to the addressbook `Entry` shape, so "commit the value of
   * the row the user clicked" is exactly right for both sources. An earlier version reimplemented
   * that for external rows and ended up a strictly worse copy: same three writes, minus the
   * cascading auto-fill the base also triggers, plus its own `isSearching` reset to remember.
   */
  override onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.#userTypedSincePick.set(false);
    super.onOptionSelected(event);
  }

  /**
   * Keeps a not-yet-saved external address valid.
   *
   * The inherited check treats "not in the loaded options" as a cue to re-fetch the value from the
   * module and, failing that, mark the field invalid. A value this field built from an external
   * suggestion has no entry id and is in no module, so that path would always fail it — and it
   * would fail it LATER than the pick, on any revalidation (navigating back, a cascading auto-fill
   * elsewhere), which is what makes it easy to miss.
   *
   * Clearing the error rather than just returning early: `FormlyErrorService.validateOptions` is
   * the only thing that ever removes `invalidOption`, so a bare return would strand an error set
   * for a PREVIOUS value. A user whose saved address had been deleted would fix it by picking an
   * external suggestion and then watch the field stay invalid and the save button stay disabled.
   */
  protected override validateOptionExists(): void {
    if (isUnsavedAddressbookEntry(this.field.formControl.value)) {
      const errors = this.field.formControl.errors;
      if (errors?.['invalidOption']) {
        delete errors['invalidOption'];
        this.field.formControl.setErrors(Object.keys(errors).length ? errors : null);
      }
      return;
    }
    super.validateOptionExists();
  }

  /**
   * Saves the picked external address into the Adressenboek module.
   *
   * Runs the module's configured create-form rather than a dialog of its own: which fields the
   * user confirms is then module configuration rather than code, and the runner does the rest — it
   * prefills from `prefill`, focuses the first field so the still-empty title is where the cursor
   * lands, and renders the form's own save action.
   *
   * Without a create-form configured on Adressenboek the runner reports `fallback` and we open the
   * full entry modal: wider than intended, but it does save, so the field keeps working on a
   * tenant that has not configured the short form yet.
   */
  addToAddressbook(): void {
    if (this.isDisabled() || !this.canAddToAddressbook()) {
      return;
    }

    const module = this.linkedData()[0]?.module;
    if (!module) {
      return;
    }

    const prefill = this.#prefillFromCurrentValue();
    const runner = this.#modalService.resolveCreateRunner(module, {
      prefill,
      formModuleId: (this.field.props?.['createFormId'] as string) || undefined,
    });

    if (runner.status === 'denied') {
      return; // the user was already told
    }

    if (runner.status !== 'fallback') {
      // `runnerDialogRef` covers 'pending' too: the modal service opens that dialog itself once the
      // ModulesStore settles, and skipping it would drop the saved entry on the floor — the field
      // would keep the unsaved external value next to the addressbook row just created from it.
      runnerDialogRef(runner)
        .pipe(
          switchMap(ref => ref.afterClosed()),
          takeUntil(this.destroy$)
        )
        .subscribe((result: ({ createdEntries?: Record<string, Entry> } & Entry) | null) => {
          if (result == null) {
            return; // dialog cancelled
          }
          // Two close shapes, same rule the generic autocomplete applies: a wizard closes with a
          // map of created entries (resolve the one from THIS field's module), a flat form closes
          // as the saved entry itself. `result.entry` is only the presence check — the entry is
          // `result`, not `result.entry`.
          const created = result.createdEntries
            ? resolveCreatedEntry(result.createdEntries, module.id)
            : (result as CreateRunnerCloseResult & Partial<Entry>).entry
              ? (result as Entry)
              : null;
          if (created) {
            this.#applySavedEntry(created);
          } else {
            this.#utilityService.showMessage('Adres toevoegen mislukt.');
          }
        });
      return;
    }

    const modal = this.#modalService.openEntryInModal(module, prefill, module.singularName, true);
    modal
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: { entry?: Entry } | null) => {
        if (result?.entry) {
          this.#applySavedEntry(result.entry);
        } else if (result != null) {
          // Same message on both paths. Whether a tenant configured the short create-form decides
          // WHICH dialog opens; it should not decide whether a failed save says anything at all.
          this.#utilityService.showMessage('Adres toevoegen mislukt.');
        }
      });
  }

  /**
   * The externally-derived value as a create-form prefill: the address to check, but NOT the title.
   *
   * `name` is dropped on purpose. The service fills it with the composed address so the row has
   * something to show in the list, but the create-form's title is the one thing the user is there
   * to supply ("hoe je dat item gaat noemen"). Leaving it populated would hand them a field that is
   * already full, under a cursor the runner puts there precisely because it should be empty.
   */
  #prefillFromCurrentValue(): Entry {
    const value = this.field.formControl.value as Entry;
    const { name: _title, ...addressFields } = (value?.entry ?? {}) as Record<string, unknown>;
    return {
      id: undefined,
      entry: { archived: false, ...addressFields },
    };
  }

  /**
   * Swaps the unsaved value for the saved entry. That single write is what makes the add-link
   * disappear (the entry now has an id) and what makes the value survive a revalidation through
   * the normal path instead of the exemption above.
   *
   * The saved entry is seeded into the options list FIRST. Once it has an id the exemption no
   * longer applies, so the very next validation pass goes looking for it in the module — a read
   * straight after the write, which Cosmos does not promise. Losing that race would flag the field
   * invalid immediately after a successful save.
   */
  #applySavedEntry(created: Entry): void {
    const linkedField = this.linkedData()[0]?.moduleLinkedField;
    const label =
      (linkedField ? (UtilityService.keyPathToValue(created, linkedField) as string) : undefined) ||
      (created.entry?.['name'] as string) ||
      this.searchField.value ||
      '';
    created.selectedInput = label;

    this.filteredOptions.update((options: AddressbookOption[]) => [
      { label, value: created, source: AddressbookOptionSourceEnum.Book },
      ...options.filter(option => option.value?.id !== created.id),
    ]);

    this.#userTypedSincePick.set(false);
    commitUserValue(this.field.formControl, created);
    this.searchField.patchValue(label, { emitEvent: false });
    this.currentValue.set(created);
    this.#utilityService.showMessage('Adres toegevoegd.');
  }
}
